import React from 'react';
import { FormattedMessage } from 'react-intl';

import {
  LinkConfiguration,
  LinkConfigurationMap,
  ProductConfiguration,
  ProductConfigurationApiBase,
  ProductConfigurationMap,
  ProductConfigurationResponse,
  ProductItemType,
} from '../clients/sites/model';
import { StaticLinkConfig } from '../ui/Products/LinkCard/helpfulLinks';
import { createRemoteIcon } from '../ui/Products/LinkCard/RemoteIcon';
import { createImageIcon } from '../ui/Products/ProductCard/ProductIcon';

export enum ProductKeys {
  JIRA_CORE = 'JIRA_CORE',
  JIRA_BUSINESS = 'JIRA_BUSINESS',
  JIRA_SERVICE_DESK = 'JIRA_SERVICE_DESK',
  JIRA_SOFTWARE = 'JIRA_SOFTWARE',
  CONFLUENCE = 'CONFLUENCE',
  OPSGENIE = 'OPSGENIE',
  BITBUCKET = 'BITBUCKET',
  TRELLO = 'TRELLO',
  STATUSPAGE = 'STATUSPAGE',
  TEAM_CENTRAL = 'TEAM_CENTRAL',
  AVOCADO = 'AVOCADO',
  COMPASS = 'COMPASS',
  ADMIN = 'ADMIN',
  WAC = 'WAC',
  MAC = 'MAC',
}
export type ProductKeysWithRemote = ProductKeys & string;

export const PRODUCT_KEYS_TO_IDENTITY_APPLICATION: { [key in ProductKeys]: ANALYTICS_PRODUCT_TYPE } = {
  ADMIN: 'admin',
  BITBUCKET: 'bitbucket',
  CONFLUENCE: 'confluence',
  JIRA_CORE: 'jira',
  JIRA_BUSINESS: 'jira',
  JIRA_SERVICE_DESK: 'jira',
  JIRA_SOFTWARE: 'jira',
  OPSGENIE: 'opsgenie',
  STATUSPAGE: 'statuspage',
  TRELLO: 'trello',
  TEAM_CENTRAL: 'teamcentral', // TODO: This will be atlas eventually
  AVOCADO: 'avocado',
  COMPASS: 'compass',
  MAC: 'mac',
  WAC: 'wac',
};

export enum WORKLENS_PRODUCT_TYPE {
  JIRA_BUSINESS = 'JIRA_BUSINESS',
  JIRA_SERVICE_DESK = 'JIRA_SERVICE_DESK',
  JIRA_SOFTWARE = 'JIRA_SOFTWARE',
  CONFLUENCE = 'CONFLUENCE',
  OPSGENIE = 'OPSGENIE',
  BITBUCKET = 'BITBUCKET',
  TRELLO = 'TRELLO',
  STATUSPAGE = 'STATUSPAGE',
  TEAM_CENTRAL = 'TEAM_CENTRAL',
  AVOCADO = 'AVOCADO',
  COMPASS = 'COMPASS',
}

export type ANALYTICS_PRODUCT_TYPE =
  | 'admin'
  | 'bitbucket'
  | 'confluence'
  | 'jira'
  | 'jira_addon'
  | 'jira_core'
  | 'jira_platform'
  | 'jira_portfolio'
  | 'jira_serviceDesk'
  | 'jira_software'
  | 'jira_unknown'
  | 'opsgenie'
  | 'sourcetree'
  | 'statuspage'
  | 'trello'
  | 'watermelon'
  | 'avocado'
  | 'compass'
  | 'teamcentral'
  | 'atlas'
  | 'townsquare'
  | 'start'
  | 'wac'
  | 'mac';

export interface ProductAndLinkConfiguration {
  links: LinkConfigurationMap;
  products: ProductConfigurationMap;
}

export const ADVANCED_SEARCH_URL: { [key: string]: (root: string, term: string) => string } = {
  BITBUCKET: (root, term) => `${root}/search?q=${term}`,
  CONFLUENCE: (root, term) => `${root}/wiki/search?text=${term}`,
  JIRA_SERVICE_DESK: (root, term) => `${root}/secure/QuickSearch.jspa?searchString=${term}`,
  JIRA_SOFTWARE: (root, term) => `${root}/secure/QuickSearch.jspa?searchString=${term}`,
};

// Analytics team is trying to standardise names of product attributes.
export const WORKLENS_TO_ANALYTICS_PRODUCT_TYPE: { [key in WORKLENS_PRODUCT_TYPE]: ANALYTICS_PRODUCT_TYPE } = {
  BITBUCKET: 'bitbucket',
  CONFLUENCE: 'confluence',
  JIRA_BUSINESS: 'jira_core',
  JIRA_SERVICE_DESK: 'jira_serviceDesk',
  JIRA_SOFTWARE: 'jira_software',
  OPSGENIE: 'opsgenie',
  STATUSPAGE: 'statuspage',
  TRELLO: 'trello',
  TEAM_CENTRAL: 'watermelon',
  AVOCADO: 'avocado',
  COMPASS: 'compass',
};

export const PRODUCT_KEY_TO_ANALYTICS_PRODUCT_TYPE: Partial<Record<string, ANALYTICS_PRODUCT_TYPE>> = {
  [ProductKeys.JIRA_SOFTWARE]: 'jira_software',
  [ProductKeys.CONFLUENCE]: 'confluence',
};

export const WORKLENS_PRODUCT_TYPE_TO_PRODUCT_TYPE: { [key in WORKLENS_PRODUCT_TYPE]: ProductKeys } = {
  BITBUCKET: ProductKeys.BITBUCKET,
  CONFLUENCE: ProductKeys.CONFLUENCE,
  JIRA_BUSINESS: ProductKeys.JIRA_CORE,
  JIRA_SERVICE_DESK: ProductKeys.JIRA_SERVICE_DESK,
  JIRA_SOFTWARE: ProductKeys.JIRA_SOFTWARE,
  OPSGENIE: ProductKeys.OPSGENIE,
  STATUSPAGE: ProductKeys.STATUSPAGE,
  TRELLO: ProductKeys.TRELLO,
  TEAM_CENTRAL: ProductKeys.TEAM_CENTRAL,
  AVOCADO: ProductKeys.AVOCADO,
  COMPASS: ProductKeys.COMPASS,
};

type JOINABLE_SITE_KEYS =
  | 'confluence.ondemand'
  | 'jira-core.ondemand'
  | 'jira-servicedesk.ondemand'
  | 'jira-software.ondemand'
  | string;
// Product types supported bu join site service
export const SUPPORTED_JOINABLE_SITES: JOINABLE_SITE_KEYS[] = [
  'confluence.ondemand',
  'jira-core.ondemand',
  'jira-servicedesk.ondemand',
  'jira-software.ondemand',
];

export const getProductConfiguration = (key: string, productTypes: ProductConfigurationMap): ProductConfiguration => {
  return productTypes[key];
};

export const getProductConfigurationFromWorklensType = (key: WORKLENS_PRODUCT_TYPE): ProductKeys =>
  WORKLENS_PRODUCT_TYPE_TO_PRODUCT_TYPE[key];

export const getProductTypeFromWorklensType = (
  key: WORKLENS_PRODUCT_TYPE,
  productTypes: ProductConfigurationMap,
): ProductConfiguration => getProductConfiguration(getProductConfigurationFromWorklensType(key), productTypes);

const formatLabel = (label: ProductConfigurationApiBase['label']): ProductItemType['label'] => {
  return typeof label === 'string' ? label : <FormattedMessage {...label} />;
};

const formatDescription = (description: ProductConfigurationApiBase['description']): ProductItemType['description'] => {
  return typeof description === 'string' ? description : description ? <FormattedMessage {...description} /> : null;
};

export const productConfigurationResponseToProductTypeMap = (
  response: ProductConfigurationResponse,
): ProductAndLinkConfiguration => ({
  links: Object.entries(response.links).reduce<LinkConfigurationMap>((acc, [key, link]) => {
    const staticLinkConfig = StaticLinkConfig[key] || {};
    const linkConfig: LinkConfiguration = {
      label: formatLabel(link.label),
      labelAnalyticKey: typeof link.label === 'string' ? link.label : link.label.id,
      description: formatDescription(link.description),
      Icon: createRemoteIcon(link.iconUrl, {
        size: 'xlarge',
      }),
      href: link.href,
      synchronisationApplication: staticLinkConfig.synchronisationApplication,
      synchroniseSession: staticLinkConfig.synchroniseSession,
    };
    return {
      ...acc,
      [key]: linkConfig,
    };
    // We want to merge remote & static config for Admin links for now
  }, StaticLinkConfig),
  products: Object.entries(response.products).reduce<ProductConfigurationMap>(
    (acc, [key, product]) => ({
      ...acc,
      [key]: {
        ...product,
        label: formatLabel(product.label),
        Icon: {
          white: createImageIcon(product.icons.white),
          blue: createImageIcon(product.icons.blue),
        },
        href: product.href,
      },
    }),
    {},
  ),
});
